import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, X, CheckCircle2, HeartHandshake } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../../services/api';
import { useNotifications } from '../../../context';
import './PlanUnlockedModal.css';

const CARE_PLAN_FEATURES = [
  'All 27 guided care questions (10–30), for every member',
  'The Daily Habit — one gentle reflection a day',
  'Team insights on the care decisions that matter',
];

const SUBSCRIPTION_FEATURES = [
  'Advocacy workbook in Full mode — all seven stages, deeper exercises',
  'Five-field Action Plan with the Check-In loop',
  'Share workbook artifacts and Care Packs with your team',
  'Crisis Quick-Access team notify',
];

// PlanUnlockedModal — global celebration shown the moment a team's plan
// settles (broadcast over WebSocket on the billing webhook). The unlock is
// the product's biggest milestone, so it gets the modal treatment — warm
// and dignified, in the spirit of MilestoneCelebration.
const PlanUnlockedModal = () => {
  const navigate = useNavigate();
  const { planUnlocked, clearPlanUnlocked } = useNotifications();
  const [ackState, setAckState] = useState('idle'); // idle | busy | done | done-quiet

  if (!planUnlocked) return null;

  const isCarePlan = planUnlocked.fee_type === 'care_plan';
  const sponsorName = planUnlocked.sponsor_display || null;

  const acceptSponsorCredit = async () => {
    setAckState('busy');
    try {
      await api.post(`/v1/sponsorship/${planUnlocked.sponsorship_id}/acknowledgment/`, { action: 'accept' });
      setAckState('done');
      toast.success(`"Sponsored by ${sponsorName}" now shows on your team`);
    } catch {
      setAckState('idle');
      toast.error('Could not save that — you can decide any time from your billing page.');
    }
  };

  const goToTeam = () => {
    const target = isCarePlan
      ? `/team/${planUnlocked.team_id}?tab=daily-habit`
      : `/team/${planUnlocked.team_id}`;
    clearPlanUnlocked();
    navigate(target);
  };

  return (
    <div className="plan-unlocked__overlay" role="dialog" aria-modal="true" aria-labelledby="plan-unlocked-title">
      <div className="plan-unlocked" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="plan-unlocked__close" aria-label="Close" onClick={clearPlanUnlocked}>
          <X size={18} />
        </button>

        <div className="plan-unlocked__burst" aria-hidden>
          <Sparkles size={26} />
        </div>

        <h3 id="plan-unlocked-title">
          {isCarePlan ? 'Your care plan is unlocked' : 'Your team is on the full plan'}
        </h3>
        <p>
          {isCarePlan ? (
            <>
              Someone just settled the Care Plan for <strong>{planUnlocked.team_name}</strong> —
              all 27 guided questions are now open for every member of your family, together.
            </>
          ) : (
            <>
              The subscription for <strong>{planUnlocked.team_name}</strong> is active — your
              family&rsquo;s shared space, recordings and care fund tools are fully open.
            </>
          )}
        </p>

        <ul className="plan-unlocked__features">
          {(isCarePlan ? CARE_PLAN_FEATURES : SUBSCRIPTION_FEATURES).map((feature) => (
            <li key={feature}>
              <CheckCircle2 size={15} aria-hidden />
              {feature}
            </li>
          ))}
        </ul>

        {sponsorName && planUnlocked.sponsorship_id && (
          <div className="plan-unlocked__sponsor">
            <HeartHandshake size={16} aria-hidden />
            {ackState === 'done' ? (
              <span>
                <strong>Sponsored with love by {sponsorName}</strong> — now shown on your team.
              </span>
            ) : ackState === 'done-quiet' ? (
              <span>No problem — the choice stays waiting on your billing page.</span>
            ) : (
              <>
                <span>
                  This gift came from <strong>{sponsorName}</strong>. Show
                  &ldquo;Sponsored by {sponsorName}&rdquo; on your team?
                </span>
                <span className="plan-unlocked__sponsor-actions">
                  <button type="button" onClick={acceptSponsorCredit} disabled={ackState === 'busy'}>
                    {ackState === 'busy' ? 'Saving…' : 'Yes, show it'}
                  </button>
                  <button
                    type="button"
                    className="plan-unlocked__sponsor-later"
                    onClick={() => setAckState('done-quiet')}
                  >
                    Decide later
                  </button>
                </span>
              </>
            )}
          </div>
        )}

        <button type="button" className="plan-unlocked__cta" onClick={goToTeam}>
          {isCarePlan ? 'Begin your Daily Habit' : 'Open the workbook'}
        </button>
        <button type="button" className="plan-unlocked__later" onClick={clearPlanUnlocked}>
          I&rsquo;ll explore later
        </button>
      </div>
    </div>
  );
};

export default PlanUnlockedModal;
